/**
 * Oppgaver — the staff work hub's tasks.  MKK adoption Batch 1 (MKK_ADOPTION_BATCH_1.md §7); hardened 2026-10-02.
 *
 * Behind the `tasks` capability and work.assigned (or tasks.manage). The order on the screen is the order of the
 * questions a person brings: what needs me now (overdue and due today, always first), what I am doing, what is open,
 * and — on request — what is finished. The due filter is a row of buttons above the list (Forfalt · I dag · Denne uken);
 * search, status and assignee fold behind one «Filter» button on a phone. The list comes fifty at a time («Vis flere»).
 * A task opens in a panel beside the list on a wide screen, and as its own view on a phone; «Lukk» / «Tilbake til
 * listen» (or Escape, when nothing is unsaved) returns, and focus goes back to the row it came from.
 *
 * Someone with only work.assigned sees their own tasks and may change their status and comment. tasks.manage adds
 * «Alle», «Ny oppgave», editing, handing on and cancelling. A change sends the version it was based on. If somebody else
 * changed the task meanwhile, the newest version is shown — and an edit the person was typing is KEPT, with the choice
 * to save it over the newer one or to drop it.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { can, post, patch, errText, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { useDirty } from '../lib/nav';
import { osloTime } from '../lib/format';
import { Btn, Card, Field, Loading, LoadError, Msg, Tag, TwoStep } from '../components/ui';
import { FormDialog } from '../components/dialog';
import { BackButton, Conflict, DueText, EventSelect, MoreButton, PersonSelect, PRIORITY, StatusTag, eventText, hashQuery, useAssignable,
  useEscapeToClose, useEvents, usePaged } from './work-ui';

/** The due filter, as buttons: overdue work is its own choice, never folded into «Denne uken» (today and six days on). */
const DUE_FILTERS: [string, string][] = [['', 'Alle frister'], ['overdue', 'Forfalt'], ['today', 'I dag'], ['week', 'Denne uken'], ['none', 'Uten frist']];
const STATUS_FILTERS: [string, string][] = [['active', 'Åpne og i gang'], ['open', 'Åpne'], ['in_progress', 'I gang'], ['done', 'Ferdige'], ['cancelled', 'Avbrutte'], ['all', 'Alle']];

export function Oppgaver() {
  const manage = can('tasks.manage');
  const start = hashQuery();
  const [scope, setScope] = useState<'mine' | 'all'>(manage && (start.get('scope') === 'all' || !can('work.assigned')) ? 'all' : 'mine');
  const [status, setStatus] = useState(start.get('status') || 'active');
  const [due, setDue] = useState(start.get('due') || '');
  const [assignee, setAssignee] = useState(manage ? start.get('assignee') || '' : '');
  const [q, setQ] = useState('');
  const [typed, setTyped] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [open, setOpen] = useState<string | null>(start.get('id'));   // a task id, or 'new'
  const lastRow = useRef<string | null>(null);
  // Owner 2026-10-23: «+ Ny oppgave» opens the form in a popup; an existing task keeps the detail beside the list.
  const [newDirty, setNewDirty] = useState(false);
  const closeNew = () => { setOpen(null); setNewDirty(false); };
  useEffect(() => { const t = setTimeout(() => setQ(typed.trim()), 250); return () => clearTimeout(t); }, [typed]);
  const qs = new URLSearchParams({ scope, status, ...(due ? { due } : {}), ...(assignee ? { assignee } : {}), ...(q ? { q } : {}) });
  const list = usePaged('/work/tasks?' + qs.toString());
  const people = useAssignable(manage);

  const close = useCallback(() => {
    setOpen(null);
    // Back to the row the person came from (or the list's heading).
    setTimeout(() => (document.getElementById(lastRow.current ? 'task-row-' + lastRow.current : 'tasks-list') as HTMLElement | null)?.focus(), 0);
  }, []);
  const items = list.items;
  const urgent = items.filter((t) => t.due === 'overdue' || t.due === 'today');
  const groups: [string, Body[]][] = [
    ['Krever handling', urgent],
    ['I gang', items.filter((t) => t.status === 'in_progress' && !urgent.includes(t))],
    ['Åpne', items.filter((t) => t.status === 'open' && !urgent.includes(t))],
    ['Ferdige', items.filter((t) => t.status === 'done')],
    ['Avbrutte', items.filter((t) => t.status === 'cancelled')],
  ];
  const filtersOn = [status !== 'active', assignee, q].filter(Boolean).length;

  return (
    <div className={'work-split' + (open && open !== 'new' ? ' has-detail' : '')}>
      <Card className="work-list" title={<span id="tasks-list" tabIndex={-1}>Oppgaver</span>}
            hint="Det du har fått i oppgave, og det som haster. Bare ansatte ser oppgaver; ingenting sendes ut herfra.">
        <div className="work-bar">
          {manage && (
            <div className="bnr-seg work-seg" role="group" aria-label="Hvem sine oppgaver">
              <button type="button" aria-pressed={scope === 'mine'} onClick={() => setScope('mine')}>Mine</button>
              <button type="button" aria-pressed={scope === 'all'} onClick={() => setScope('all')}>Alle</button>
            </div>
          )}
          <span className="sp" />
          {manage && <Btn onClick={() => { lastRow.current = null; setOpen('new'); }}>+ Ny oppgave</Btn>}
        </div>
        <div className="work-bar">
          <div className="bnr-seg work-seg work-due-seg" role="group" aria-label="Frist">
            {DUE_FILTERS.map(([v, l]) => <button key={v} type="button" aria-pressed={due === v} onClick={() => setDue(v)}>{l}</button>)}
          </div>
        </div>
        <div className="work-bar">
          <Field label="Søk" className="field work-search">{(id) => (
            <input id={id} type="search" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Tittel eller beskrivelse" maxLength={100} />
          )}</Field>
          <Btn ghost className="btn sm ghost work-filter-toggle" aria-expanded={filtersOpen} aria-controls="task-filters" onClick={() => setFiltersOpen(!filtersOpen)}>
            Filter{filtersOn ? ` (${filtersOn})` : ''}
          </Btn>
        </div>
        <div id="task-filters" className={'work-filters' + (filtersOpen ? ' open' : '')}>
          <Field label="Status">{(id) => (
            <select id={id} value={status} onChange={(e) => setStatus(e.target.value)}>{STATUS_FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          )}</Field>
          {manage && scope === 'all' && (
            <Field label="Ansvarlig">{(id) => (
              <select id={id} value={assignee} onChange={(e) => setAssignee(e.target.value)}>
                <option value="">Alle</option><option value="none">Uten ansvarlig</option>
                {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}</Field>
          )}
        </div>
        {list.error ? <LoadError error={list.error} retry={list.reload} /> : !list.data ? <Loading /> : (
          <>
            <p className="vh" role="status">{list.total === 1 ? '1 oppgave' : `${list.total} oppgaver`}{items.length < list.total ? `, ${items.length} vist` : ''}</p>
            {items.length === 0 && <p className="card-body work-empty">{filtersOn || due ? 'Ingen oppgaver passer filteret.' : scope === 'mine' ? 'Du har ingen åpne oppgaver.' : 'Ingen åpne oppgaver.'}</p>}
            {groups.filter(([, g]) => g.length).map(([label, g]) => (
              <section key={label} className="work-group" aria-label={`${label}: ${g.length}`}>
                <h4>{label} <span className="shop-count">{g.length}</span></h4>
                <ul className="work-rows">{g.map((t) => (
                  <li key={t.id}>
                    <button type="button" id={'task-row-' + t.id} className={'work-row' + (open === t.id ? ' sel' : '')} aria-current={open === t.id ? 'true' : undefined}
                            onClick={() => { lastRow.current = t.id; setOpen(t.id); }}>
                      <span className="work-row-title">{t.priority === 'high' && <span className="work-flag">Høy prioritet: </span>}{t.title}</span>
                      <span className="work-row-meta">
                        <StatusTag status={t.status} />
                        <DueText dueOn={t.dueOn} due={t.due} />
                        <span>{t.assignee ? t.assignee.name + (t.assignee.active ? '' : ' (deaktivert)') : 'Ingen ansvarlig'}</span>
                        {t.event && <span>· {eventText(t.event)}</span>}
                      </span>
                    </button>
                  </li>
                ))}</ul>
              </section>
            ))}
            <MoreButton list={list} />
          </>
        )}
      </Card>
      <FormDialog open={open === 'new'} title="Ny oppgave" dirty={newDirty} onClose={closeNew}>
        {open === 'new' && <TaskForm people={people} onDirty={setNewDirty} onCancel={closeNew}
          onSaved={(id) => { void list.reload(); setNewDirty(false); lastRow.current = id; setOpen(id); }} />}
      </FormDialog>
      {open && open !== 'new' && <TaskDetail key={open} id={open} manage={manage} people={people} onClose={close} onChanged={() => void list.reload()} />}
    </div>
  );
}

/* ---- a new task (in the popup: its title, «×» and Escape are FormDialog's) ---- */
function TaskForm({ people, onCancel, onSaved, onDirty }: {
  people: { id: string; name: string }[]; onCancel: () => void; onSaved: (id: string) => void; onDirty: (dirty: boolean) => void;
}) {
  const ev = useEvents();
  const [f, setF] = useState({ title: '', description: '', priority: 'normal', dueOn: '', assigneeId: '', eventId: '' });
  const [msg, say] = useMsg();
  const [busy, setBusy] = useState(false);
  const dirty = Boolean(f.title || f.description);
  useDirty('task-new', dirty);
  useEffect(() => { onDirty(dirty); }, [dirty, onDirty]);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true); say('Lagrer …');
    const r = await post('/work/tasks', { title: f.title, description: f.description || null, priority: f.priority, dueOn: f.dueOn || null,
      assigneeId: f.assigneeId || null, ...(ev.allowed && f.eventId ? { eventId: f.eventId } : {}) });
    setBusy(false);
    if (!r.ok) return say(errText(r.body, { title: 'Tittel', description: 'Beskrivelse', dueOn: 'Frist', assigneeId: 'Ansvarlig', eventId: 'Arrangement' }), 'err');
    setF({ title: '', description: '', priority: 'normal', dueOn: '', assigneeId: '', eventId: '' });
    onSaved(r.body.task.id);
  };
  return (
    <div>
        <Field label="Tittel" required>{(id) => <input id={id} value={f.title} onChange={set('title')} maxLength={160} />}</Field>
        <Field label="Beskrivelse" note="Valgfritt. Skriv ikke mer om andre enn det oppgaven trenger.">{(id, nid) => (
          <textarea id={id} rows={4} value={f.description} onChange={set('description')} maxLength={4000} aria-describedby={nid} />
        )}</Field>
        <div className="row">
          <Field label="Frist">{(id) => <input id={id} type="date" value={f.dueOn} onChange={set('dueOn')} />}</Field>
          <Field label="Prioritet">{(id) => (
            <select id={id} value={f.priority} onChange={set('priority')}><option value="low">Lav</option><option value="normal">Normal</option><option value="high">Høy</option></select>
          )}</Field>
        </div>
        <Field label="Ansvarlig">{(id) => <PersonSelect id={id} value={f.assigneeId} onChange={(v) => setF({ ...f, assigneeId: v })} people={people} />}</Field>
        {ev.allowed && <Field label="Arrangement">{(id) => <EventSelect id={id} value={f.eventId} onChange={(v) => setF({ ...f, eventId: v })} events={ev.items} />}</Field>}
        <div className="row"><Btn small={false} disabled={busy} aria-busy={busy || undefined} onClick={save}>{busy ? 'Lagrer …' : 'Lagre oppgaven'}</Btn><Btn ghost onClick={onCancel}>Avbryt</Btn></div>
        <Msg text={msg.text} tone={msg.tone} />
    </div>
  );
}

/* ---- one task ---- */
function TaskDetail({ id, manage, people, onClose, onChanged }: {
  id: string; manage: boolean; people: { id: string; name: string }[]; onClose: () => void; onChanged: () => void;
}) {
  const { data, error, reload, setData } = useLoad<Body>('/work/tasks/' + id);
  const [msg, say] = useMsg();
  const [busy, setBusy] = useState(false);
  // A conflict on an EDIT: the form stays as typed, and the person chooses (Conflict). editKey rebuilds the form from
  // the task as it is now — after a save, or when they drop their edit.
  const [conflict, setConflict] = useState(false);
  const [editKey, setEditKey] = useState(0);
  const [dirty, setDirtyState] = useState({ edit: false, comment: false });
  const head = useRef<HTMLHeadingElement>(null);
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (data && !focused) { head.current?.focus(); setFocused(true); } }, [data, focused]);
  useEscapeToClose(head, onClose, dirty.edit || dirty.comment || busy);
  if (error) return <Card className="work-detail"><div className="card-body"><BackButton onClick={onClose} what="oppgaven" /></div><LoadError error={error} retry={reload} /></Card>;
  if (!data) return <Card className="work-detail"><Loading /></Card>;
  const t: Body = data.task;

  /** Every change: the version it was based on, and the newest version back on a conflict. */
  const change = async (body: Body, done: string, fromEdit = false) => {
    setBusy(true); say('Lagrer …');
    const r = await patch('/work/tasks/' + id, { ...body, version: t.version });
    setBusy(false);
    if (r.status === 409 && r.body.task) {
      setData({ task: r.body.task, comments: r.body.comments, can: r.body.can });
      if (fromEdit) { setConflict(true); return say('', ''); }
      return say(r.body.message + ' Endringen din ble ikke lagret.', 'warn');
    }
    if (!r.ok) return say(errText(r.body, { title: 'Tittel', dueOn: 'Frist', assigneeId: 'Ansvarlig', status: 'Status' }), 'err');
    setData(r.body); setConflict(false); if (fromEdit) setEditKey((k) => k + 1); onChanged(); say(done, 'ok');
  };
  const statusButtons: [string, string, string][] = t.status === 'open' ? [['in_progress', 'Start', 'Oppgaven er i gang.'], ['done', 'Ferdig', 'Oppgaven er ferdig.']]
    : t.status === 'in_progress' ? [['done', 'Ferdig', 'Oppgaven er ferdig.'], ['open', 'Sett på pause', 'Oppgaven er satt tilbake til åpen.']]
    : t.status === 'done' ? [['open', 'Gjenåpne', 'Oppgaven er åpnet igjen.']] : manage ? [['open', 'Gjenopprett', 'Oppgaven er gjenopprettet.']] : [];

  return (
    <Card className="work-detail" title={<span ref={head} tabIndex={-1}>{t.title}</span>}>
      <div className="card-body">
        <BackButton onClick={onClose} what="oppgaven" />
        <p className="work-row-meta">
          <StatusTag status={t.status} />
          {t.priority !== 'normal' && <Tag tone={PRIORITY[t.priority][1]}>Prioritet: {PRIORITY[t.priority][0]}</Tag>}
          <DueText dueOn={t.dueOn} due={t.due} />
        </p>
        {/* Batch 1 remediation: a stored day that is not a real one (older data) is said, not shown as a date. */}
        {(t.invalidDates || []).includes('dueOn') && (
          <div className="msg warn" role="note">Fristen som er lagret ({t.dueOn}) er ikke en gyldig dato.{data.can && data.can.edit ? ' Velg en ny frist under «Endre oppgaven», eller lagre uten frist.' : ' Be den som styrer oppgavene om å rette den.'}</div>
        )}
        <dl className="kv">
          <dt>Ansvarlig</dt><dd>{t.assignee ? t.assignee.name + (t.assignee.active ? '' : ' (deaktivert — gi den til en annen)') : 'Ingen'}</dd>
          {t.event && <><dt>Arrangement</dt><dd>{eventText(t.event)}</dd></>}
          <dt>Opprettet</dt><dd>{osloTime(t.createdAt)}{t.createdByName ? ' av ' + t.createdByName : ''}</dd>
          {t.doneAt && <><dt>Ferdig</dt><dd>{osloTime(t.doneAt)}</dd></>}
        </dl>
        {t.description && <p className="work-desc">{t.description}</p>}
        {data.can && data.can.status && statusButtons.length > 0 && (
          <div className="row" role="group" aria-label="Endre status">
            {statusButtons.map(([s, label, said]) => <Btn key={s} disabled={busy} onClick={() => change({ status: s }, said)}>{label}</Btn>)}
            {manage && (t.status === 'open' || t.status === 'in_progress') && (
              <TwoStep label="Avbryt oppgaven" confirmLabel="Ja, avbryt den" disabled={busy} onConfirm={() => change({ status: 'cancelled' }, 'Oppgaven er avbrutt.')} />
            )}
          </div>
        )}
        <Msg text={msg.text} tone={msg.tone} />
        {manage && <TaskEdit key={t.id + ':' + editKey} task={t} people={people} busy={busy} conflict={conflict}
          onDirty={(d) => setDirtyState((x) => (x.edit === d ? x : { ...x, edit: d }))}
          onSave={(b) => change(b, 'Endringen er lagret.', true)}
          onDiscard={() => { setConflict(false); setEditKey((k) => k + 1); say('Endringene dine er forkastet. Dette er den nyeste versjonen.', 'ok'); }} />}
        <Comments id={id} comments={data.comments || []} onAdded={(d) => setData(d)} onDirty={(d) => setDirtyState((x) => (x.comment === d ? x : { ...x, comment: d }))} />
      </div>
    </Card>
  );
}

function TaskEdit({ task, people, busy, conflict, onDirty, onSave, onDiscard }: {
  task: Body; people: { id: string; name: string }[]; busy: boolean; conflict: boolean;
  onDirty: (dirty: boolean) => void; onSave: (b: Body) => void; onDiscard: () => void;
}) {
  const ev = useEvents();
  // The task as it was when the form was built: what "unsaved" is measured against. Not rebuilt when a newer version
  // arrives (a conflict) — that is what kept the person's typing.
  // A stored due day that is not a real one starts empty here (the detail says why), so saving never resends it.
  const [init] = useState(() => ({ title: task.title, description: task.description || '', priority: task.priority,
    dueOn: (task.invalidDates || []).includes('dueOn') ? '' : task.dueOn || '', assigneeId: task.assigneeId || '', eventId: task.eventId || '' }));
  const [f, setF] = useState(init);
  const [open, setOpen] = useState(conflict);
  const dirty = JSON.stringify(f) !== JSON.stringify(init);
  useDirty('task-edit', dirty);
  useEffect(() => { onDirty(dirty); }, [dirty, onDirty]);
  useEffect(() => { if (conflict) setOpen(true); }, [conflict]);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  // The person who has it, even when they are no longer someone work can be handed to — so the select can show them.
  const list = task.assignee && !people.some((p) => p.id === task.assignee.id) ? [...people, { id: task.assignee.id, name: task.assignee.name + ' (kan ikke få nye oppgaver)' }] : people;
  // The event link is sent only by someone who may see events: without events.write it is left as it is.
  const body = () => ({ title: f.title, description: f.description || null, priority: f.priority, dueOn: f.dueOn || null,
    assigneeId: f.assigneeId || null, ...(ev.allowed ? { eventId: f.eventId || null } : {}) });
  return (
    <details className="work-edit" open={open} onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary>Endre oppgaven{dirty ? ' (ikke lagret)' : ''}</summary>
      {conflict && <Conflict what="oppgaven" busy={busy} onKeepMine={() => onSave(body())} onTakeTheirs={onDiscard} />}
      <Field label="Tittel" required>{(id) => <input id={id} value={f.title} onChange={set('title')} maxLength={160} />}</Field>
      <Field label="Beskrivelse">{(id) => <textarea id={id} rows={4} value={f.description} onChange={set('description')} maxLength={4000} />}</Field>
      <div className="row">
        <Field label="Frist">{(id) => <input id={id} type="date" value={f.dueOn} onChange={set('dueOn')} />}</Field>
        <Field label="Prioritet">{(id) => (
          <select id={id} value={f.priority} onChange={set('priority')}><option value="low">Lav</option><option value="normal">Normal</option><option value="high">Høy</option></select>
        )}</Field>
      </div>
      <Field label="Ansvarlig">{(id) => <PersonSelect id={id} value={f.assigneeId} onChange={(v) => setF({ ...f, assigneeId: v })} people={list} />}</Field>
      {ev.allowed && <Field label="Arrangement">{(id) => <EventSelect id={id} value={f.eventId} onChange={(v) => setF({ ...f, eventId: v })} events={ev.items} />}</Field>}
      {!conflict && <Btn disabled={!dirty || busy} aria-busy={busy || undefined} onClick={() => onSave(body())}>{busy ? 'Lagrer …' : 'Lagre endringene'}</Btn>}
    </details>
  );
}

function Comments({ id, comments, onAdded, onDirty }: { id: string; comments: Body[]; onAdded: (d: Body) => void; onDirty: (dirty: boolean) => void }) {
  const [text, setText] = useState('');
  const [msg, say] = useMsg();
  const [busy, setBusy] = useState(false);
  const dirty = Boolean(text.trim());
  useDirty('task-comment', dirty);
  useEffect(() => { onDirty(dirty); }, [dirty, onDirty]);
  const add = async () => {
    setBusy(true);
    const r = await post(`/work/tasks/${id}/comments`, { body: text });
    setBusy(false);
    if (!r.ok) return say(errText(r.body), 'err');
    setText(''); onAdded(r.body); say('Kommentaren er lagt til.', 'ok');
  };
  return (
    <section className="work-comments" aria-labelledby={'comments-' + id}>
      <h4 id={'comments-' + id}>Kommentarer <span className="shop-count">{comments.length}</span></h4>
      {comments.length === 0 && <p className="note">Ingen kommentarer ennå.</p>}
      <ol>{comments.map((c) => (
        <li key={c.id}><b>{c.author ? c.author.name : 'Ukjent'}</b> <span className="note">{osloTime(c.createdAt)}</span><p>{c.body}</p></li>
      ))}</ol>
      <Field label="Ny kommentar">{(fid) => <textarea id={fid} rows={2} value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} />}</Field>
      <Btn disabled={!dirty || busy} aria-busy={busy || undefined} onClick={add}>{busy ? 'Lagrer …' : 'Legg til kommentar'}</Btn>
      <Msg text={msg.text} tone={msg.tone} />
    </section>
  );
}
